import { useContext, useState } from "react"
import { Check, ChevronDown } from "lucide-react"
import AdHocCommands from "@/api/adHocCommands"
import { XmppContext } from "@/stream/xmppClient"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { cn } from "@/lib/utils"
import {
  carShortName,
  crewChatName,
  driverDisplayName,
  type CrewRow,
} from "@/lib/crewBoard"
import type { Profile } from "@/reducers/profilesSlice"
import { useAppSelector } from "@/reducers/hooks"
import { selectDriverNameOrder } from "@/reducers/serviceConfigSlice"
import { driverName } from "@/lib/driverNameOrder"
import { saveCrewChat } from "./saveCrewChat"
import { usePendingPick } from "./usePendingPick"
import { useTranslation } from "react-i18next"

type CrewDriverCellProps = {
  row: CrewRow
  drivers: Profile[]
  vehicleOfDriver: Map<string, CrewRow>
}

const bare = (jid: string): string => jid.split("/")[0].toLowerCase()

export const CrewDriverCell = ({
  row,
  drivers,
  vehicleOfDriver,
}: CrewDriverCellProps) => {
  const { t } = useTranslation()
  const { client } = useContext(XmppContext)
  const nameOrder = useAppSelector(selectDriverNameOrder)
  const [open, setOpen] = useState(false)
  const [confirmTarget, setConfirmTarget] = useState<Profile | null>(null)
  const [confirmRemove, setConfirmRemove] = useState(false)

  const here = carShortName(row)
  const currentJid = row.driver ? bare(row.driver.jid) : ""
  const {
    pending: pendingJid,
    saving,
    save: savePick,
  } = usePendingPick(currentJid)

  const otherVehicleOf = (driver: Profile): CrewRow | undefined => {
    const other = vehicleOfDriver.get(bare(driver.jid))
    return other && other.car.id !== row.car.id ? other : undefined
  }

  const save = (driver: Profile, from: CrewRow | undefined) =>
    savePick(bare(driver.jid), async () => {
      await saveCrewChat(client, {
        row,
        name: crewChatName(row.car, driver, row.route),
        driverJid: driver.jid,
      })
      if (from) {
        window.appToaster.show({
          message: t("fleet.crewBoard.moved", {
            driver: driverDisplayName(driver),
            from: carShortName(from),
            to: here,
          }),
          intent: "success",
          timeout: 3000,
        })
      }
    })

  const remove = () =>
    savePick("", async () => {
      if (!row.chat) return
      await AdHocCommands.upsertMuc({
        client,
        mucJid: row.chat.jid,
        name: crewChatName(row.car, undefined, row.route),
        clearDriver: true,
      })
    })

  const handleRemove = () => {
    setOpen(false)
    if ((pendingJid ?? currentJid) === "") return
    setConfirmRemove(true)
  }

  const handleSelect = (driver: Profile) => {
    setOpen(false)
    if (bare(driver.jid) === (pendingJid ?? currentJid)) {
      return
    }
    const other = otherVehicleOf(driver)
    if (other?.doubleCrew) return
    if (other) {
      setConfirmTarget(driver)
      return
    }
    void save(driver, undefined)
  }

  const shown =
    pendingJid !== undefined
      ? drivers.find((d) => bare(d.jid) === pendingJid)
      : row.driver
  const confirmFrom = confirmTarget ? otherVehicleOf(confirmTarget) : undefined
  // Only a chat can lose its driver; a car without one has nobody to take out.
  const canRemove = !!row.chat && !!shown

  return (
    <div className="flex items-center gap-2">
      {/* Not modal: a modal list sets the body's pointer-events, and the
          confirmation dialog (another copy of Radix's dismissable layer)
          would remember "none" while the list is still closing and restore
          it when it closes, leaving the page dead. */}
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-label={t("fleet.crewBoard.driverFor", { vehicle: here })}
            aria-expanded={open}
            disabled={saving || row.doubleCrew}
            className="w-full justify-between font-normal"
          >
            <span className={cn("truncate", !shown && "text-muted-foreground")}>
              {shown ? driverDisplayName(shown) : t("fleet.crewBoard.none")}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-[9999] w-[var(--radix-popover-trigger-width)] min-w-[260px] p-0"
          sideOffset={5}
        >
          <Command>
            <CommandInput placeholder={t("fleet.crewBoard.searchDriver")} />
            <CommandList className="max-h-[300px] overflow-y-auto">
              <CommandEmpty>{t("fleet.crewBoard.noDriverFound")}</CommandEmpty>
              <CommandGroup>
                {canRemove && (
                  <CommandItem
                    value={t("fleet.crewBoard.none")}
                    onSelect={handleRemove}
                  >
                    <Check className="mr-2 h-4 w-4 opacity-0" />
                    <span className="text-muted-foreground">
                      {t("fleet.crewBoard.none")}
                    </span>
                  </CommandItem>
                )}
                {drivers.map((driver) => {
                  const other = otherVehicleOf(driver)
                  return (
                    <CommandItem
                      key={driver.jid}
                      // Both orders, so "Jan Kow" and "Kowalski" find the same driver.
                      value={`${driverName(driver, "first_last")} ${driverName(driver, "last_first")} ${driver.jid}`}
                      // Double crew chats are support-managed and upsert_muc
                      // leaves them alone: the driver would end up in two cars.
                      disabled={other?.doubleCrew}
                      onSelect={() => handleSelect(driver)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          shown && bare(shown.jid) === bare(driver.jid)
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                      {driverName(driver, nameOrder)}
                      {other && (
                        <span className="text-muted-foreground">
                          {" "}
                          {t(
                            other.doubleCrew
                              ? "fleet.crewBoard.otherVehicleDoubleCrew"
                              : "fleet.crewBoard.otherVehicle",
                            { vehicle: carShortName(other) },
                          )}
                        </span>
                      )}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {row.doubleCrew && (
        <span className="shrink-0 text-xs text-muted-foreground">
          {t("fleet.crewBoard.doubleCrew")}
        </span>
      )}
      <AlertDialog
        open={confirmTarget !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setConfirmTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("fleet.crewBoard.moveTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmTarget && confirmFrom
                ? t("fleet.crewBoard.moveDescription", {
                    driver: driverDisplayName(confirmTarget),
                    from: carShortName(confirmFrom),
                    to: here,
                  })
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmTarget) void save(confirmTarget, confirmFrom)
                setConfirmTarget(null)
              }}
            >
              {t("fleet.crewBoard.move")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("fleet.crewBoard.removeTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {shown
                ? t("fleet.crewBoard.removeDescription", {
                    driver: driverDisplayName(shown),
                    vehicle: here,
                  })
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmRemove(false)
                void remove()
              }}
            >
              {t("common.remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
